function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTooltip, TooltipPopup } from "@reach/tooltip";
import { useTransition, animated } from "react-spring/web.cjs";
import { useId, toPx, isIcon } from "../../helpers";
animated.TooltipPopup = animated(TooltipPopup);
function BaseTooltip(_ref) {
  let {
    children,
    label,
    ariaLabel,
    on,
    debug,
    hasArrow,
    ...rest
  } = _ref;
  const [trigger, tooltip, isVisible] = useTooltip({
    ...children.props,
    DEBUG_STYLE: debug
  });
  let prevLabel = useRef("").current;
  useEffect(() => {
    prevLabel = label;
  }, [label, isVisible]);
  let triggerProps = {
    ...trigger,
    /*
     * Temporary Fix
     * There is a bug in reach/tooltip where onBlur does not get wrapped correctly.
     * This fixes it.
     */
    onBlur: e => {
      trigger.onBlur(e);
      if (children.props.onBlur) {
        children.props.onBlur(e);
      }
    }
  };
  switch (on) {
    case "hover":
      {
        var _children$props, _children$props2, _children$props3;
        triggerProps.onFocus = (_children$props = children.props) === null || _children$props === void 0 ? void 0 : _children$props.onFocus;
        triggerProps.onBlur = (_children$props2 = children.props) === null || _children$props2 === void 0 ? void 0 : _children$props2.onBlur;
        triggerProps.onMouseDown = (_children$props3 = children.props) === null || _children$props3 === void 0 ? void 0 : _children$props3.onMouseDown;
        break;
      }
    case "click":
      {
        var _children$props4, _children$props5, _children$props6;
        triggerProps.onMouseEnter = (_children$props4 = children.props) === null || _children$props4 === void 0 ? void 0 : _children$props4.onMouseEnter;
        triggerProps.onMouseLeave = (_children$props5 = children.props) === null || _children$props5 === void 0 ? void 0 : _children$props5.onMouseLeave;
        triggerProps.onMouseMove = (_children$props6 = children.props) === null || _children$props6 === void 0 ? void 0 : _children$props6.onMouseMove;
        break;
      }
  }
  const transitions = useTransition(isVisible ? tooltip : false, null, {
    from: {
      opacity: 0
    },
    enter: {
      opacity: 1
    },
    leave: {
      opacity: 0
    },
    config: {
      mass: 1,
      tension: 500,
      friction: 40
    }
  });
  const displayLabel = label || prevLabel;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.cloneElement(React.Children.only(children), triggerProps), transitions.map(_ref2 => {
    let {
      item: tooltip,
      props: styles,
      key
    } = _ref2;
    return tooltip && /*#__PURE__*/React.createElement(animated.TooltipPopup, _extends({
      key: key,
      ariaLabel: ariaLabel
    }, tooltip, rest, {
      style: styles,
      label: displayLabel ? /*#__PURE__*/React.createElement(React.Fragment, null, displayLabel, /*#__PURE__*/React.createElement("div", {
        className: "arrow",
        style: {
          display: hasArrow ? null : "none"
        }
      })) : ""
    }));
  }));
}
BaseTooltip.defaultProps = {
  on: "any",
  label: undefined,
  ariaLabel: undefined,
  debug: false,
  hasArrow: true
};
BaseTooltip.propTypes = {
  on: PropTypes.oneOf(["click", "hover", "any"]),
  children: PropTypes.node.isRequired,
  label: PropTypes.node,
  ariaLabel: PropTypes.string,
  debug: PropTypes.bool,
  hasArrow: PropTypes.bool
};
const shouldAppearAbove = (collisions, defaultPosition) => {
  return defaultPosition === "bottom" && !collisions.top || collisions.bottom;
};
const Tooltip = _ref3 => {
  let {
    defaultPosition,
    ...props
  } = _ref3;
  const id = `tui-tooltip-${useId()}`;
  return /*#__PURE__*/React.createElement(BaseTooltip, _extends({}, props, {
    id: id,
    className: classNames(props.variant, {
      [props.className]: props.className
    }),
    style: props.label ? props.style : {
      display: "none"
    },
    hasArrow: defaultPosition !== "right",
    position: (triggerRect, tooltipRect) => {
      const offset = 10; // distance between trigger and tooltip
      const offsetWidth = 2;
      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      const left = triggerCenter - tooltipRect.width / 2;
      const maxLeft = document.documentElement.clientWidth - tooltipRect.width - offsetWidth;
      const tooltipElement = document.querySelector(`#tooltip--${id}`);
      const tooltipArrowElement = tooltipElement.querySelector(".arrow");
      if (!tooltipArrowElement) return {};

      // reset classes
      tooltipElement.classList.remove("top", "bottom", "right", "left");
      tooltipArrowElement.classList.remove("above", "right");
      let collisions = {
        top: triggerRect.top - tooltipRect.height < 0,
        bottom: window.innerHeight < (defaultPosition === "right" ? triggerRect.top : triggerRect.bottom) + tooltipRect.height,
        right: document.documentElement.clientWidth < triggerCenter + tooltipRect.width / 2 + offsetWidth,
        left: triggerCenter - tooltipRect.width / 2 - offsetWidth < 0
      };
      if (defaultPosition === "right") {
        collisions.right = document.documentElement.clientWidth < triggerRect.right + tooltipRect.width + offsetWidth;
      }
      const horizontalPosition = (() => {
        if (collisions.right) return "left";
        if (collisions.left) return "right";
        return "center";
      })();
      const isAbove = shouldAppearAbove(collisions, defaultPosition);
      tooltipElement.classList.add(isAbove ? "top" : "bottom");
      let tooltipLeft, top;
      if (defaultPosition !== "right") {
        let arrowLeft, arrowRight;
        switch (horizontalPosition) {
          case "left":
            tooltipLeft = triggerCenter - tooltipRect.width;
            arrowLeft = "unset";
            arrowRight = 0;
            tooltipElement.classList.add("left");
            break;
          case "right":
          default:
            tooltipLeft = Math.min(Math.max(2, left), maxLeft) + window.scrollX;
            arrowLeft = triggerCenter - tooltipArrowElement.getBoundingClientRect().width / 2 - tooltipRect.left;
            arrowRight = "unset";
        }
        tooltipArrowElement.style.left = toPx(arrowLeft);
        tooltipArrowElement.style.right = toPx(arrowRight);
        top = isAbove ? triggerRect.top - offset - tooltipRect.height + window.scrollY : triggerRect.bottom + offset + window.scrollY;
      } else {
        tooltipLeft = triggerRect.left + triggerRect.width + offset;
        const triggerVerticalCenter = triggerRect.top + triggerRect.height / 2;
        const rowHeight = 36;
        top = triggerVerticalCenter - rowHeight / 2;
        if (isAbove) {
          top = triggerRect.bottom - tooltipRect.height;
        }
        if (collisions.right) {
          tooltipLeft = triggerRect.left - tooltipRect.width - offset;
        }
      }
      return {
        left: tooltipLeft,
        top
      };
    }
  }), isIcon(props.children) ? /*#__PURE__*/React.createElement("span", null, props.children) : props.children);
};
Tooltip.displayName = "Tooltip";
Tooltip.defaultProps = {
  label: undefined,
  variant: "dark",
  className: "",
  style: {},
  position: styles => styles,
  on: "any",
  debug: false,
  defaultPosition: "bottom"
};
Tooltip.propTypes = {
  label: PropTypes.node,
  variant: PropTypes.oneOf(["light", "dark"]),
  className: PropTypes.string,
  children: PropTypes.element.isRequired,
  style: PropTypes.object,
  position: PropTypes.func,
  on: PropTypes.oneOf(["click", "hover", "any"]),
  debug: PropTypes.bool,
  defaultPosition: PropTypes.oneOf(["bottom", "top", "right"])
};
export default Tooltip;
//# sourceMappingURL=Tooltip.js.map